// src/components/MyTutorReviewPanel.jsx
//
// Slice B (Model-B): Tutor Review on the shared /serviceRequests lifecycle. Credit-/bundle-
// FREE -- any signed-in student submits an essay; Danielle quotes a per-submission fee, the
// student pays off-platform (PayMe/FPS, quoting the billReference), then receives written
// feedback. Replaces the old localStorage + bundle-quota UI.
//
// Reads /serviceRequests where type=='tutor_review' && uid==me via useMyServiceRequests.
// Submit writes {type:'tutor_review', uid, status:'requested', payload:{submissionText,...}}.
import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { CONTENT_TYPE_LABELS } from '../config/contentTypes.js';
import PaymentInstructionsBlock from './PaymentInstructionsBlock.jsx';
import ServiceCardPayment from './ServiceCardPayment.jsx';
import { useMyServiceRequests } from '../storage/useServiceRequests.js';

const MAX_TEXT = 50000;
const STATUS = {
    requested: { cls: 'bg-amber-500/20 text-amber-300', label: 'Submitted · awaiting quote' },
    quoted:    { cls: 'bg-indigo-500/20 text-indigo-300', label: 'Quote received · pay to confirm' },
    paid:      { cls: 'bg-green-500/20 text-green-300', label: 'Paid · review in progress' },
    completed: { cls: 'bg-green-600/20 text-green-300', label: 'Reviewed' },
    rejected:  { cls: 'bg-red-500/20 text-red-300', label: 'Declined' },
};
function money(cents) { return 'HK$' + (Number(cents || 0) / 100).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt(ts) {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB');
}
const DEFAULT_FEE_NOTE =
    'Submit an essay or exam answer for a personal review by Danielle. She reads it and sends you a fee quote; ' +
    'you pay by card through Stripe or PayMe/FPS, then receive written feedback here. There is no subscription — you pay per submission.';

const MyTutorReviewPanel = ({ state }) => {
    const { currentUser } = useAuth();
    const { requests, loading } = useMyServiceRequests();
    const reviews = requests.filter((r) => r.type === 'tutor_review');

    const settings = state.tutorReviewSettings || {};
    const feeNote = (typeof settings.feeDescription === 'string' && settings.feeDescription.trim()) || DEFAULT_FEE_NOTE;
    const contentTypeOptions = Object.entries(CONTENT_TYPE_LABELS || {});

    const [text, setText] = useState('');
    const [title, setTitle] = useState('');
    const [contentType, setContentType] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [openId, setOpenId] = useState(null); // expanded submission text

    const submit = async () => {
        setError(null);
        if (AUTH_MODE === 'mock' || !db) { setError('Tutor review needs the live app (emulator or firebase mode).'); return; }
        const uid = currentUser?.uid;
        if (!uid) { setError('Please sign in to submit.'); return; }
        if (!text.trim()) { setError('Paste your essay text to submit.'); return; }
        if (text.length > MAX_TEXT) { setError(`Your text is too long (max ${MAX_TEXT.toLocaleString()} characters).`); return; }
        setSubmitting(true);
        try {
            const payload = { submissionText: text };
            if (title.trim()) payload.contentTitle = title.trim();
            if (contentType) payload.contentType = contentType;
            await addDoc(collection(db, 'serviceRequests'), {
                type: 'tutor_review', uid, status: 'requested', payload, createdAt: serverTimestamp()
            });
            setText(''); setTitle(''); setContentType('');
        } catch (e) {
            setError((e && e.message) || 'Could not submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Fee explanation */}
            <div className="border border-midnight-accent/40 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent">
                <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">Tutor Review by Danielle</div>
                <p className="font-serif italic text-midnight-muted text-sm leading-relaxed whitespace-pre-line">{feeNote}</p>
            </div>

            {/* Submit form */}
            <div className="border border-midnight-accent/20 bg-midnight-page/40 p-6 space-y-3">
                <div className="font-display text-midnight-accent text-xs font-semibold">Submit for tutor review</div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Title (optional)</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Macbeth ambition essay"
                            className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent" />
                    </div>
                    <div>
                        <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Type (optional)</label>
                        <select value={contentType} onChange={(e) => setContentType(e.target.value)}
                            className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent">
                            <option value="">— select —</option>
                            {contentTypeOptions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Your essay / answer</label>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} maxLength={MAX_TEXT}
                        placeholder="Paste your full essay or exam answer here…"
                        className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm leading-relaxed focus:outline-none focus:border-midnight-accent resize-y" />
                    <div className="font-serif italic text-midnight-muted text-xs mt-1">{text.length.toLocaleString()}/{MAX_TEXT.toLocaleString()} characters</div>
                </div>
                {error && <p className="font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>}
                <button onClick={submit} disabled={submitting || !text.trim()}
                    className="px-5 py-2.5 bg-midnight-accent text-midnight-pageDark font-display text-xs hover:bg-midnight-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                    {submitting ? 'Submitting…' : 'Submit for tutor review'}
                </button>
            </div>

            {/* Submissions list */}
            <div>
                <div className="font-display text-midnight-accent text-xs mb-3 font-semibold">My submissions</div>
                {loading ? (
                    <p className="font-serif italic text-midnight-muted text-sm">Loading…</p>
                ) : reviews.length === 0 ? (
                    <p className="font-serif italic text-midnight-muted text-sm">No submissions yet.</p>
                ) : (
                    <div className="space-y-3">
                        {reviews.map((r) => {
                            const p = r.payload || {};
                            const badge = STATUS[r.status] || { cls: 'bg-midnight-muted/20 text-midnight-muted', label: r.status };
                            const expanded = openId === r.id;
                            return (
                                <div key={r.id} className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="min-w-0">
                                            <div className="font-display text-midnight-text text-sm">{p.contentTitle || (p.contentType && CONTENT_TYPE_LABELS[p.contentType]) || 'Submission'}</div>
                                            <div className="font-serif italic text-midnight-muted text-xs">
                                                Submitted {fmt(r.createdAt)}{r.billReference ? ' · Ref ' + r.billReference : ''}
                                            </div>
                                        </div>
                                        <span className={`px-3 py-1.5 font-display text-sm whitespace-nowrap font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                                    </div>

                                    <button onClick={() => setOpenId(expanded ? null : r.id)}
                                        className="font-serif italic text-midnight-muted text-xs hover:text-midnight-accent">
                                        {expanded ? 'Hide my submission' : 'View my submission'}
                                    </button>
                                    {expanded && <p className="mt-2 font-serif text-midnight-text text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-midnight-accent/30 pl-3">{p.submissionText}</p>}

                                    {r.status === 'quoted' && (
                                        <div className="mt-3 border-t border-midnight-accent/15 pt-3 space-y-3">
                                            <div className="font-serif text-midnight-text text-sm">Fee quote: <span className="font-display text-midnight-accent">{money(r.quotedAmount)}</span></div>
                                            <ServiceCardPayment request={r} />
                                            <details><summary className="cursor-pointer text-sm">Prefer PayMe / FPS?</summary><PaymentInstructionsBlock
                                                booking={state.bookingContent}
                                                referenceCode={r.billReference}
                                                prefilledMessage={`Tutor review payment ${money(r.quotedAmount)}, reference ${r.billReference}`}
                                                theme="midnight" />
                                            <p className="font-serif italic text-midnight-muted text-xs">
                                                Pay via PayMe / FPS above quoting reference <span className="text-midnight-accent">{r.billReference}</span>, then await confirmation.
                                            </p></details>
                                        </div>
                                    )}
                                    {r.status === 'paid' && <p className="font-serif italic text-midnight-muted text-xs mt-2">Payment confirmed — Danielle is reviewing your work.</p>}
                                    {r.status === 'completed' && (
                                        <div className="mt-3 border-t border-midnight-accent/15 pt-3">
                                            <div className="font-display text-midnight-accent text-xs mb-1 font-semibold">Danielle's feedback</div>
                                            <p className="font-serif text-midnight-text text-sm leading-relaxed whitespace-pre-wrap">{r.feedback || '—'}</p>
                                        </div>
                                    )}
                                    {r.status === 'rejected' && r.rejectReason && <p className="font-serif text-red-300 text-xs mt-2">Reason: {r.rejectReason}</p>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyTutorReviewPanel;
